import {
  BatteryPowerType,
  ChartColorContext,
  ChartErrorComponent,
  defaultEChartsOptionForInterval,
  getBatteryPowerLinesOption,
  ECHARTS_CONTAINER_HEIGHT as height,
  NEGATIVE_BAND_AXIS,
  nowMarkLineSeriesOption,
  POSITIVE_BAND_AXIS,
} from '@e2m-customer-portal/domains';
import { useLocale, useTokenInfo } from '@e2m-customer-portal/hooks';
import { convertMeasure, isDefined, isMeasureWithValue, UserRole } from '@e2m-customer-portal/shared';
import { eachMinuteOfInterval } from 'date-fns';
import { EChartsOption, SeriesOption } from 'echarts';
import { EChartsType, MarkAreaOption, YAXisOption } from 'echarts/types/dist/shared';
import {
  Datapoint,
  DatapointCollection,
  DatapointType,
  Device,
  Forecast,
  ForecastSlice,
  InputFieldType,
  Interval,
} from 'generated/graphql';
import { ReactElement, useContext, useMemo, useRef } from 'react';
import EChartsReact, { EChartsReactRef } from 'react-echarts-library';
import { useTranslation } from 'react-i18next';
import { ChartColor, getOutages, getOutagesData } from './utils';
import { ApolloError } from '@apollo/client';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';

type AcceptedForecastSlice = {
  date: Date;
  datapoints: Datapoint[];
};

/**
 * Filters the passed forecast slices by the passed datapoint type and returns the data in an array.
 * The first tuple value is the unix timestamp, the second value is the band’s value and third value is
 * the second value negated, for use with mirrored bands like FCR.
 * @param datapointType The band to be collected.
 * @param slices The forecast slices to source from.
 * @param unit The target unit of the values, by default `kW`.
 * @returns An array of [unix timestamp, band value, negated band value].
 */
function acceptedForecastData(datapointType: DatapointType, slices?: AcceptedForecastSlice[], unit: string = 'kW') {
  return slices?.map((slice) => {
    const field = slice.datapoints.find((datapoint) => datapoint.type === datapointType);

    const fieldValue = field?.value == null ? null : convertMeasure({ value: field.value, unit: 'MW' }, unit).value;

    if (fieldValue == null) return [slice.date.getTime(), null, null];

    return [slice.date.getTime(), fieldValue, -fieldValue];
  });
}

function availableBands(slices?: AcceptedForecastSlice[], power?: BatteryPowerType, unit: string = 'kW') {
  if (!power) return [];

  return slices?.map((slice) => {
    const positiveFields = slice.datapoints.filter(
      (datapoint) =>
        datapoint.type === DatapointType.FCR_BAND ||
        datapoint.type === DatapointType.AFRR_BAND_POSITIVE ||
        datapoint.type === DatapointType.MFRR_BAND_POSITIVE,
    );
    const negativeFields = slice.datapoints.filter(
      (datapoint) =>
        datapoint.type === DatapointType.FCR_BAND ||
        datapoint.type === DatapointType.AFRR_BAND_NEGATIVE ||
        datapoint.type === DatapointType.MFRR_BAND_NEGATIVE,
    );

    const positiveFieldValue = positiveFields.reduce(
      (total, datapoint) =>
        total - (datapoint.value == null ? 0 : convertMeasure({ value: datapoint.value, unit: 'MW' }, unit).value),
      convertMeasure({ value: power.power_discharge, unit: 'MW' }, unit).value,
    );
    const negativeFieldValue = negativeFields.reduce(
      (total, datapoint) =>
        total - (datapoint.value == null ? 0 : convertMeasure({ value: datapoint.value, unit: 'MW' }, unit).value),
      -convertMeasure({ value: power.power_charge, unit: 'MW' }, unit).value,
    );

    return [slice.date.getTime(), positiveFieldValue, negativeFieldValue];
  });
}

/**
 * Filters the passed forecast slices by the passed input field type and returns the data in an array.
 * The first tuple value is the unix timestamp, the second value is the band’s value and third value is
 * the second value negated, for use with mirrored bands like FCR.
 * @param inputFieldType The band to be collected.
 * @param slices The forecast slices to source from.
 * @param unit The target unit of the values, by default `kW`.
 * @returns An array of [unix timestamp, band value, negated band value].
 */
function submittedForecastData(
  inputFieldType: InputFieldType,
  slices: readonly ForecastSlice[],
  unit: string = 'kW',
): Array<[number, number | null, number | null]> {
  return slices.map((slice) => {
    const field = slice.fields.find((field) => field.type === inputFieldType);

    const fieldValue = isMeasureWithValue(field?.value) ? convertMeasure(field!.value, unit).value : null;

    return [slice.date.getTime(), fieldValue, fieldValue == null ? null : -fieldValue];
  });
}

interface ControlEnergyChartProps {
  dataInterval: Interval;
  device: Device;
  forecasts?: ReadonlyArray<Forecast>;
  forecastsError?: ApolloError;
  acceptedForecasts?: DatapointCollection;
  acceptedForecastError?: ApolloError;
  power?: BatteryPowerType;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  isZoomed?: boolean;
}

export function ControlEnergyChart({
  dataInterval,
  device,
  forecasts,
  forecastsError,
  acceptedForecasts,
  acceptedForecastError,
  power,
  onChartReadyCallback,
  resetZoom,
  isZoomed,
}: Readonly<ControlEnergyChartProps>): ReactElement<ControlEnergyChartProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const chartColorVariant = useContext(ChartColorContext);
  const chartRef = useRef<EChartsReactRef>(null);

  const { roles } = useTokenInfo();
  const isVPPOperator = roles.has(UserRole.VPPOperator);

  const deviceAcceptedForecast =
    acceptedForecasts &&
    [...acceptedForecasts.timeSlices]
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
      .map((timeSlice) => ({
        date: timeSlice.timestamp,
        datapoints: timeSlice.entities
          .filter((entity) => entity.number === +device.id)
          .flatMap((entity) => entity.datapoints),
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

  const availableBandsData = availableBands(deviceAcceptedForecast, power);

  const deviceSubmittedForecast = [
    ...(forecasts?.find((forecast) => forecast.deviceId === device.id)?.slices ?? []),
  ].sort((a, b) => a.date.getTime() - b.date.getTime());

  const batteryPowerLinesOption = useMemo(
    () => getBatteryPowerLinesOption(t, dataInterval, power),
    [dataInterval, power],
  );

  const valueFormatter = (negate?: boolean) => (value: unknown) => {
    if (value == null) return '-';
    return `${(negate ? -value : +value).toLocaleString(locale)} kW`;
  };

  const outagesData = getOutagesData(deviceSubmittedForecast);
  const outagesMarkedAreas = getOutages(deviceSubmittedForecast);

  const commonYAxisOption: YAXisOption = {
    type: 'value',
    alignTicks: true,
    show: false,
    inverse: true,
    min: 'dataMin',
    max: 'dataMax',
  };

  const bandZeroPoint: SeriesOption = {
    type: 'line',
    stack: 'accepted',
    lineStyle: {
      cap: 'butt',
      color: '#424242',
      type: 'dashed',
      width: power ? 1 : 0,
    },
    endLabel: {
      color: '#424242',
      align: 'right',
      textShadowColor: '#F9F9F9',
      textShadowBlur: 5,
      textShadowOffsetX: 0,
      textShadowOffsetY: 0,
      padding: 1,
      show: !!power,
    },
    step: 'end',
    symbol: 'none',
    showSymbol: false,
    tooltip: { show: false },
  };

  const commonSeriesOption: SeriesOption = {
    type: 'line',
    areaStyle: {},
    step: 'end',
    symbol: 'circle',
    showSymbol: false,
  };

  const availableBandSeriesOption: SeriesOption = {
    type: 'line',
    data: availableBandsData,
    color: '#00000000',
    lineStyle: {
      width: 0,
    },
    symbol: 'none',
    showSymbol: false,
    tooltip: {
      valueFormatter: valueFormatter(false),
    },
  };

  const showAcceptedFCRBand = deviceAcceptedForecast?.some((slice) =>
    slice.datapoints.some((field) => field.type === DatapointType.FCR_BAND),
  );
  const showAcceptedAFRRPosBand = deviceAcceptedForecast?.some((slice) =>
    slice.datapoints.some((field) => field.type === DatapointType.AFRR_BAND_POSITIVE),
  );
  const showAcceptedAFRRNegBand = deviceAcceptedForecast?.some((slice) =>
    slice.datapoints.some((field) => field.type === DatapointType.AFRR_BAND_NEGATIVE),
  );
  const showSubmittedFCRBand =
    isVPPOperator &&
    deviceSubmittedForecast.some((slice) => slice.fields.some((field) => field.type === InputFieldType.FCR));
  const showSubmittedAFRRPosBand =
    isVPPOperator &&
    deviceSubmittedForecast.some((slice) => slice.fields.some((field) => field.type === InputFieldType.AFRR_POSITIVE));
  const showSubmittedAFRRNegBand =
    isVPPOperator &&
    deviceSubmittedForecast.some((slice) => slice.fields.some((field) => field.type === InputFieldType.AFRR_NEGATIVE));

  const acceptedFCRSeries: SeriesOption = {
    ...commonSeriesOption,
    data: acceptedForecastData(DatapointType.FCR_BAND, deviceAcceptedForecast),
    name: t('COLOCATION_OVERVIEW_CHARTS_ACCEPTED_FCR'),
    stack: 'accepted',
    color: ChartColor.PrimaryFull[chartColorVariant],
    yAxisId: POSITIVE_BAND_AXIS,
    tooltip: { show: showAcceptedFCRBand, valueFormatter: valueFormatter(false) },
    z: 5,
  };

  const submittedFCRSeries: SeriesOption = {
    ...commonSeriesOption,
    data: showSubmittedFCRBand ? submittedForecastData(InputFieldType.FCR, deviceSubmittedForecast) : undefined,
    name: t('COLOCATION_OVERVIEW_CHARTS_SUBMITTED_FCR'),
    stack: 'submitted',
    color: ChartColor.Primary60[chartColorVariant],
    yAxisId: POSITIVE_BAND_AXIS,
    lineStyle: {
      ...commonSeriesOption.lineStyle,
      type: 'dashed',
    },
    tooltip: { show: showSubmittedFCRBand, valueFormatter: valueFormatter(false) },
  };

  const defaultEChartsOption = defaultEChartsOptionForInterval(t, dataInterval);
  const option: EChartsOption = {
    ...defaultEChartsOption,
    legend: {
      ...defaultEChartsOption.legend,
      data: [
        t('COLOCATION_OVERVIEW_CHARTS_WHOLESALE'),
        showAcceptedFCRBand ? t('COLOCATION_OVERVIEW_CHARTS_ACCEPTED_FCR') : undefined,
        showAcceptedAFRRPosBand ? t('COLOCATION_OVERVIEW_CHARTS_ACCEPTED_AFRR_POSITIVE') : undefined,
        showAcceptedAFRRNegBand ? t('COLOCATION_OVERVIEW_CHARTS_ACCEPTED_AFRR_NEGATIVE') : undefined,
        showSubmittedFCRBand ? t('COLOCATION_OVERVIEW_CHARTS_SUBMITTED_FCR') : undefined,
        showSubmittedAFRRPosBand ? t('COLOCATION_OVERVIEW_CHARTS_SUBMITTED_AFRR_POSITIVE') : undefined,
        showSubmittedAFRRNegBand ? t('COLOCATION_OVERVIEW_CHARTS_SUBMITTED_AFRR_NEGATIVE') : undefined,
        outagesData.some((item) => item !== null) ? t('DOWNTIME_SET') : undefined,
      ].filter(isDefined),
    },
    group: 'group1',
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: '{value} kW',
          color: '#888888',
        },
        splitLine: {
          lineStyle: {
            color: '#F0F0F0',
          },
        },
        min: 'dataMin',
        max: 'dataMax',
      },
      {
        ...commonYAxisOption,
        id: POSITIVE_BAND_AXIS,
        dataMin: 0,
        dataMax: power ? power.power_discharge * 1000 - power.power_charge * 1000 : 0,
        min: 0,
      },
      {
        ...commonYAxisOption,
        id: NEGATIVE_BAND_AXIS,
        dataMin: power ? power.power_charge * 1000 - power.power_discharge * 1000 : 0,
        dataMax: 0,
        max: 0,
      },
    ],
    series: [
      {
        ...commonSeriesOption,
        data: submittedForecastData(InputFieldType.POWER_FEED, deviceSubmittedForecast),
        name: t('COLOCATION_OVERVIEW_CHARTS_WHOLESALE'),
        areaStyle: undefined,
        color: ChartColor.TertiaryFull[chartColorVariant],
        tooltip: { show: true, valueFormatter: valueFormatter(false) },
      },
      {
        ...bandZeroPoint,
        yAxisId: POSITIVE_BAND_AXIS,
        data: eachMinuteOfInterval(dataInterval, { step: 15 }).map((date) => [
          date.getTime(),
          0,
          convertMeasure({ value: power?.power_discharge, unit: 'MW' }, 'kW').value,
        ]),
        endLabel: {
          ...bandZeroPoint.endLabel,
          formatter: `${t('COLOCATION_OVERVIEW_CHARTS_POWER_DISCHARGE', { value: '{@[2]}' })}`,
          verticalAlign: 'top',
          offset: [-12, 5],
        },
        z: 10,
      },
      {
        ...bandZeroPoint,
        yAxisId: NEGATIVE_BAND_AXIS,
        data: eachMinuteOfInterval(dataInterval, { step: 15 }).map((date) => [
          date.getTime(),
          0,
          convertMeasure({ value: power?.power_charge, unit: 'MW' }, 'kW').value,
        ]),
        endLabel: {
          ...bandZeroPoint.endLabel,
          formatter: `${t('COLOCATION_OVERVIEW_CHARTS_POWER_CHARGE', { value: '{@[2]}' })}`,
          verticalAlign: 'bottom',
          offset: [-12, -5],
        },
        z: 10,
      },
      acceptedFCRSeries,
      {
        ...acceptedFCRSeries,
        yAxisId: NEGATIVE_BAND_AXIS,
        encode: { y: 2 },
        tooltip: { show: false },
      },
      {
        ...commonSeriesOption,
        data: acceptedForecastData(DatapointType.AFRR_BAND_POSITIVE, deviceAcceptedForecast),
        name: t('COLOCATION_OVERVIEW_CHARTS_ACCEPTED_AFRR_POSITIVE'),
        stack: 'accepted',
        color: ChartColor.SecondaryFull[chartColorVariant],
        yAxisId: POSITIVE_BAND_AXIS,
        tooltip: { show: showAcceptedAFRRPosBand, valueFormatter: valueFormatter(false) },
        z: 5,
      },
      {
        ...commonSeriesOption,
        data: acceptedForecastData(DatapointType.AFRR_BAND_NEGATIVE, deviceAcceptedForecast),
        name: t('COLOCATION_OVERVIEW_CHARTS_ACCEPTED_AFRR_NEGATIVE'),
        stack: 'accepted',
        color: ChartColor.SecondaryFull[chartColorVariant],
        yAxisId: NEGATIVE_BAND_AXIS,
        // Use the negated value to move the negative control energy band below 0 and flip the value again in the tooltip
        encode: { y: 2 },
        tooltip: { show: showAcceptedAFRRNegBand, valueFormatter: valueFormatter(true) },
        z: 5,
      },
      submittedFCRSeries,
      {
        ...submittedFCRSeries,
        yAxisId: NEGATIVE_BAND_AXIS,
        encode: { y: 2 },
        tooltip: { show: false },
      },
      {
        ...commonSeriesOption,
        data: showSubmittedAFRRPosBand
          ? submittedForecastData(InputFieldType.AFRR_POSITIVE, deviceSubmittedForecast)
          : undefined,
        name: t('COLOCATION_OVERVIEW_CHARTS_SUBMITTED_AFRR_POSITIVE'),
        stack: 'submitted',
        color: ChartColor.Secondary60[chartColorVariant],
        yAxisId: POSITIVE_BAND_AXIS,
        lineStyle: {
          ...commonSeriesOption.lineStyle,
          type: 'dashed',
        },
        tooltip: { show: showSubmittedAFRRPosBand, valueFormatter: valueFormatter(false) },
      },
      {
        ...commonSeriesOption,
        data: showSubmittedAFRRNegBand
          ? submittedForecastData(InputFieldType.AFRR_NEGATIVE, deviceSubmittedForecast)
          : undefined,
        name: t('COLOCATION_OVERVIEW_CHARTS_SUBMITTED_AFRR_NEGATIVE'),
        stack: 'submitted',
        color: ChartColor.Secondary60[chartColorVariant],
        yAxisId: NEGATIVE_BAND_AXIS,
        lineStyle: {
          ...commonSeriesOption.lineStyle,
          type: 'dashed',
        },
        // Use the negated value to move the negative control energy band below 0 and flip the value again in the tooltip
        encode: { y: 2 },
        tooltip: { show: showSubmittedAFRRNegBand, valueFormatter: valueFormatter(true) },
      },
      {
        ...availableBandSeriesOption,
        name: t('COLOCATION_OVERVIEW_CHARTS_AVAILABLE_POSITIVE_BAND'),
      },
      {
        ...availableBandSeriesOption,
        name: t('COLOCATION_OVERVIEW_CHARTS_AVAILABLE_NEGATIVE_BAND'),
        encode: {
          y: 2,
        },
      },
      ...(batteryPowerLinesOption ?? []),
      {
        type: 'line',
        lineStyle: {
          width: 0,
        },
        name: t('DOWNTIME_SET'),
        connectNulls: false,
        symbol: 'square',
        showSymbol: false,
        color: '#FF0A1190',
        tooltip: {
          valueFormatter: () => '',
        },
        data: outagesData,
        markArea: {
          label: { show: false },
          z: 9,
          itemStyle: {
            color: '#FF0A11',
            opacity: 0.5,
          },
          data: outagesMarkedAreas
            .map((s) => {
              if (s.isOutage) return [{ xAxis: +s.interval.start }, { xAxis: +s.interval.end }];
            })
            .filter(isDefined),
        } as MarkAreaOption,
      } satisfies SeriesOption,
      nowMarkLineSeriesOption,
    ],
  };

  return (
    <div className="relative">
      {isZoomed && (
        <Button
          className="absolute top-8 left-32 w-fit px-1 bg-white/45 z-40 "
          color="inherit"
          style={{ color: ChartColor.PrimaryFull[chartColorVariant] }}
          variant="outlined"
          onClick={() => chartRef.current && resetZoom(chartRef.current)}
        >
          <ZoomOutIcon style={{ fill: ChartColor.PrimaryFull[chartColorVariant] }} />
        </Button>
      )}
      <EChartsReact
        style={{ height }}
        option={option}
        opts={{ locale, renderer: 'svg' }}
        data-testid="control-energy-chart"
        onChartReady={onChartReadyCallback}
        ref={chartRef}
      />
      {(forecastsError || acceptedForecastError) && (
        <ChartErrorComponent errors={[forecastsError, acceptedForecastError]} />
      )}
    </div>
  );
}
